import { Subject } from 'rxjs';

/**
 * Diagnostic context captured when an API request comes back 401.
 *
 * It carries only what we need to tell an *expected* 401 (a stale/missing
 * bearer we recover from) apart from an *unexpected* one (our token plumbing
 * failed, or the backend rejected a token Firebase still considers valid).
 * It never carries the token itself — only its non-sensitive claims. See
 * MC-11571 and handle401Effect.
 */
export interface IUnauthorizedErrorContext {
	/** HTTP method of the failed request (upper-case). */
	method?: string;
	/** Request path, query string stripped. */
	path?: string;
	/** Whether an `Authorization: Bearer` header was attached to the request. */
	hadBearer: boolean;
	/** `exp` claim (unix seconds) of the attached token, if any. */
	tokenExp?: number;
	/** `auth_time` claim (unix seconds) of the attached token, if any. */
	tokenAuthTime?: number;
	/** `user_id`/`sub` claim of the attached token, if any. */
	tokenUserId?: string;
	/** Response body, stringified and truncated, for backend error context. */
	responseBody?: string;
}

/**
 * Subject for broadcasting 401 Unauthorized errors.
 * This allows the axios interceptor to notify the business logic layer
 * about authentication failures without creating circular dependencies.
 *
 * When a 401 error is detected, the axios interceptor emits the request
 * context on this subject, and the business logic effect decides whether the
 * session is genuinely gone (logging out) and reports the unexpected cases.
 */
export const unauthorizedError$ = new Subject<IUnauthorizedErrorContext>();
